import { ChangeDetectionStrategy, Component, ElementRef, Input, signal } from "@angular/core";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25IfShowDirective } from "../../standalone/s25.if.show.directive";
import { DataIngestionModule } from "../data-ingestion/data.ingestion.module";
import { SeHomeComponent } from "./se.home.component";
import { IngestionPointCategory } from "../data-ingestion/data.ingestion.service";
import { MyWorkflowPageComponent } from "../workflow/my.workflow.page.component";
import { WorkflowProcessesPageComponent } from "../workflow/workflow.processes.page.component";
import { WorkflowInitRulesComponent } from "../workflow/workflow.init.rules.component";
import { WorkflowActionHistoryPageComponent } from "../workflow/actions/workflow.action.history.page.component";
import { BudgetAdminComponent } from "./budget/admin/budget.admin.component";
import { BudgetRulesComponent } from "./budget/budget.rules.component";
import { S25CheckboxModule } from "../s25-checkbox/s25.checkbox.module";
import { S25ChatComponent } from "../../standalone/s25.chat.component";
import { ApprovalGroupsComponent } from "../approval-groups/approval.groups.component";
import { StudentEngagementService } from "./student.engagement.service";
import { WorkflowAdminComponent } from "../workflow/admin/workflow.admin.component";
import { SeSearchPageComponent } from "./search/se.search.page.component";
import { SeConfigComponent } from "./se.config.component";
import { SeSecurityComponent } from "./securityConfig/se.security.config.component";
import { SeBreadcrumbComponent } from "./se.breadcrumb.component";
import { SeSidebarComponent } from "./se.sidebar.component";

@TypeManagerDecorator("se-main-menu-items")
@Component({
    selector: "se-main-menu-items",
    template: `
        @if (activeMode()) {
            <div *s25IfShow="activeMode() === 'search'">
                <s25-ng-se-search-page></s25-ng-se-search-page>
            </div>

            <div *s25IfShow="activeMode() === 'chat'">
                <s25-chat [width]="'80%'" [height]="'600px'"></s25-chat>
            </div>

            <div *s25IfShow="activeMode() === 'forms'">
                <h2>Form Management</h2>
                <s25-ng-ingestion-admin
                    class="ngBlock c-margin-top--single"
                    [categoryIn]="IngestionPointCategory.ENGAGEMENT"
                ></s25-ng-ingestion-admin>
            </div>

            <div *s25IfShow="activeMode() === 'myForms'">
                <h2>My forms</h2>
                <s25-ng-ingestion-dashboard
                    class="ngBlock c-margin-top--single"
                    [selectedCategory]="IngestionPointCategory.ENGAGEMENT"
                ></s25-ng-ingestion-dashboard>
            </div>

            <div *s25IfShow="activeMode() === 'dash'">
                <s25-ng-se-home [(activeMode)]="activeMode"></s25-ng-se-home>
            </div>

            <div *s25IfShow="activeMode() === 'budget'">
                <s25-ng-budget-admin></s25-ng-budget-admin>
            </div>

            <div *s25IfShow="activeMode() === 'budgetRules'">
                <s25-ng-budget-rules></s25-ng-budget-rules>
            </div>

            <div *s25IfShow="activeMode() === 'myWorkflow'">
                <my-workflow-page></my-workflow-page>
            </div>

            <div *s25IfShow="activeMode() === 'workflow'">
                <workflow-processes-page></workflow-processes-page>
            </div>

            <div *s25IfShow="activeMode() === 'workflowRules'">
                <workflow-init-rules></workflow-init-rules>
            </div>

            <div *s25IfShow="activeMode() === 'workflowActionHistory'">
                <workflow-action-history-page></workflow-action-history-page>
            </div>

            <div *s25IfShow="activeMode() === 'groups'">
                <approval-groups></approval-groups>
            </div>

            <div *s25IfShow="activeMode() === 'workflowAdmin'">
                <s25-ng-workflow-admin></s25-ng-workflow-admin>
            </div>

            <div *s25IfShow="activeMode() === 'config'">
                <s25-ng-se-config></s25-ng-se-config>
            </div>

            <div *s25IfShow="activeMode() === 'security'">
                <s25-ng-se-security></s25-ng-se-security>
            </div>
        } @else {
            <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25LoadingInlineModule,
        S25IfShowDirective,
        DataIngestionModule,
        SeHomeComponent,
        MyWorkflowPageComponent,
        WorkflowProcessesPageComponent,
        WorkflowInitRulesComponent,
        WorkflowActionHistoryPageComponent,
        BudgetAdminComponent,
        BudgetRulesComponent,
        S25CheckboxModule,
        S25ChatComponent,
        ApprovalGroupsComponent,
        WorkflowAdminComponent,
        SeSearchPageComponent,
        SeConfigComponent,
        SeSecurityComponent,
    ],
    providers: [StudentEngagementService, S25IfShowDirective, SeSidebarComponent, SeBreadcrumbComponent],
})
export class SeMainMenuItemsComponent {
    activeMode = signal<string>("dash");

    @Input() set mode(mode: string) {
        if (mode) this.activeMode.set(mode);
    }

    modeSetter: (mode: string) => void;

    constructor(private elementRef: ElementRef) {
        this.elementRef.nativeElement.angBridge = this;

        // Set up setter function for AngularJS to call
        this.modeSetter = (value: string) => {
            if (this.activeMode() !== value) this.activeMode.set(value);
        };
    }

    protected readonly IngestionPointCategory = IngestionPointCategory;
}
